import { FC, useEffect, useMemo, useRef, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { useGetCountryListForDropdowns } from "../../../../../../../../api/services/client/clientApis";
import {
  FormController,
  MuiBox,
  MuiGrid,
  MuiSelect,
  MuiTextField,
} from "../../../../../../../../components";
import {
  CountryField,
  CountryFieldDetails,
  TextFieldWithValidation,
} from "../../../../../../../../components/textFieldWithValidation/TextFieldWithValidation";
import {
  MAX_LENGTH_50,
  MAX_LENGTH_75,
} from "../../../../../../../../helpers/constants";
import { getFieldRuleByCountry } from "../../../../../../../../helpers/constants/countryFieldRulesUtils";
import { commonSiteCoreKeys } from "../../../../../../../../helpers/constants/siteCoreKeys";
import {
  getVatPostalDetailsFromCountryKey,
  setApiDataToDropDownOption,
} from "../../../../../../../../helpers/utils";
import { useGridContainer } from "../../../../../../../../hooks/useGridContainer";
import { useNavigationSiteCore } from "../../../../../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../../../../../hooks/useSiteCore";
import { siteCoreContentLoadingSelector } from "../../../../../../../../redux/slices/appState/appStateSelector";
import { ParticipantTabFormKey } from "../../../../../../modelsAndSchemas/Models";
import { programSiteCoreKeys } from "../../../../../../siteCore/SiteCoreKeys";

interface ParticipantAddressDetailsFormProps {
  countryVatPostalData?: CountryField[];
  pageId: string;
  isEditProgram?: boolean;
  isPendingParticipant?: boolean;
  isCityDisable?: boolean;
  deliveryCountry: string;
}

export const ParticipantAddressDetails: FC<
  ParticipantAddressDetailsFormProps
> = ({
  countryVatPostalData,
  pageId,
  isEditProgram,
  isPendingParticipant,
  isCityDisable = false,
  deliveryCountry,
}) => {
  const siteCoreLoading = useSelector(siteCoreContentLoadingSelector);
  const {
    fetchLabel,
    commonLabels,
    fetchValidationMessage,
    fetchDropdownValuesByPrefix,
    commonValidationMessages,
  } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const { gridSizeMedium: gridSize, gridSizeMediumClass } = useGridContainer();

  const { control, getValues, setValue, clearErrors } = useFormContext();

  const [isDefaultPostalCodeSet, setIsDefaultPostalCodeSet] = useState(false);

  const location = useLocation();
  const isFromNIS = location.state?.isFromNISParticipant ?? false;

  const postalCodeRule = getFieldRuleByCountry(
    deliveryCountry,
    ParticipantTabFormKey.postalCode,
  );

  const watchPostalCode = useWatch({
    name: ParticipantTabFormKey.postalCode,
    control,
  });
  const { data: countryListData } = useGetCountryListForDropdowns();

  const postalCodeRef = useRef<{
    setDefaultValue: (defaultValue: string) => void;
  }>(null);

  useEffect(() => {
    if (!isDefaultPostalCodeSet && (isFromNIS || isEditProgram)) {
      if (watchPostalCode) {
        postalCodeRef.current?.setDefaultValue(watchPostalCode ?? "");
        setValue(ParticipantTabFormKey.postalCode, watchPostalCode ?? "");
        setIsDefaultPostalCodeSet(true);
      }
    }
  }, [isEditProgram, watchPostalCode, isFromNIS, isDefaultPostalCodeSet]);

  const getLabel = (key: string) => {
    return fetchLabel(programSiteCoreKeys.addressDetails, key);
  };

  const defaultCountryData: CountryFieldDetails = {
    label: getLabel(programSiteCoreKeys.addressDetailsPostalCode),
    placeHolder: commonLabels.inputPlaceHolder,
    maxLength: 10,
    regex: ".*",
    regexNotAllowed: "",
    formatErrorMessage: "",
    blurErrorMessage: "",
    isMandatory: false,
  };

  const countryIdWatch = useWatch({
    name: ParticipantTabFormKey.countryId,
    control,
  });

  const stateProvinceRule = useMemo(() => {
    const stateRule = getFieldRuleByCountry(
      countryIdWatch,
      ParticipantTabFormKey.stateProvince,
    );
    return stateRule;
  }, [countryIdWatch]);

  const postalCodeFieldDetails = useMemo(() => {
    const { countryId } = getValues();

    const postalData =
      getVatPostalDetailsFromCountryKey(countryId, countryVatPostalData)
        ?.postalCode || defaultCountryData;

    return {
      ...postalData,
      isMandatory: postalCodeRule.mandatory, // Override isMandatory as country specific
    };
  }, [countryIdWatch, countryVatPostalData, deliveryCountry]);

  const countrySpecificOptions = useMemo(() => {
    const { countryId } = getValues();

    const stateOptions = fetchDropdownValuesByPrefix(
      programSiteCoreKeys.addressDetails,
      programSiteCoreKeys.stateRegionProvinceListPrefix,
      countryId,
    );

    return { stateOptions };
  }, [countryIdWatch, siteCoreLoading]);

  const handleCountryChange = () => {
    setValue(ParticipantTabFormKey.stateProvince, "");
    clearErrors(ParticipantTabFormKey.stateProvince);
  };

  const fieldSpecialCharactersTooltipMessage = fetchValidationMessage(
    commonSiteCoreKeys.fieldSpecialCharactersTooltipMessage,
  );
  const tooltipHeader = fetchValidationMessage(
    commonSiteCoreKeys.allowedSpecialCharacters,
  );

  return (
    <MuiBox className="address-details" data-testid="address-details">
      <MuiGrid container className="main-container">
        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <FormController
            control={control}
            name={ParticipantTabFormKey.address1}
          >
            <MuiTextField
              id={`${pageId}-${ParticipantTabFormKey.address1}`}
              label={getLabel(programSiteCoreKeys.addressDetailsAddress1)}
              placeholder={commonLabels.inputPlaceHolder}
              maxLength={MAX_LENGTH_50}
              showCloseIcon
              requiredARIALabel={commonARIALabel.required}
              tooltipMessage={
                commonValidationMessages.fieldSpecialCharactersTooltipMessage
              }
              tooltipHeaderTitle={
                commonValidationMessages.allowedSpecialCharactersTitle
              }
            />
          </FormController>
        </MuiGrid>

        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <FormController
            control={control}
            name={ParticipantTabFormKey.address2}
          >
            <MuiTextField
              id={`${pageId}-${ParticipantTabFormKey.address2}`}
              label={getLabel(programSiteCoreKeys.addressDetailsAddress2)}
              placeholder={commonLabels.inputPlaceHolder}
              maxLength={MAX_LENGTH_50}
              showCloseIcon
              requiredARIALabel={commonARIALabel.required}
              tooltipMessage={
                commonValidationMessages.fieldSpecialCharactersTooltipMessage
              }
              tooltipHeaderTitle={
                commonValidationMessages.allowedSpecialCharactersTitle
              }
            />
          </FormController>
        </MuiGrid>

        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <FormController
            control={control}
            name={ParticipantTabFormKey.address3}
          >
            <MuiTextField
              id={`${pageId}-${ParticipantTabFormKey.address3}`}
              label={getLabel(programSiteCoreKeys.addressDetailsAddress3)}
              placeholder={commonLabels.inputPlaceHolder}
              maxLength={MAX_LENGTH_50}
              showCloseIcon
              requiredARIALabel={commonARIALabel.required}
              tooltipMessage={
                commonValidationMessages.fieldSpecialCharactersTooltipMessage
              }
              tooltipHeaderTitle={
                commonValidationMessages.allowedSpecialCharactersTitle
              }
            />
          </FormController>
        </MuiGrid>

        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <FormController
            control={control}
            name={ParticipantTabFormKey.countryId}
          >
            <MuiSelect
              label={getLabel(programSiteCoreKeys.addressDetailsCountry)}
              options={setApiDataToDropDownOption(countryListData, true)}
              visibleDefaultSelectOption
              value=""
              id={`${pageId}-${ParticipantTabFormKey.countryId}`}
              placeholder={commonLabels.selectPlaceHolder}
              requiredARIALabel={commonARIALabel.required}
              showSearch
              searchPlaceholder={commonLabels.search}
              onChange={handleCountryChange}
            />
          </FormController>
        </MuiGrid>

        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <FormController control={control} name={ParticipantTabFormKey.city}>
            <MuiTextField
              id={`${pageId}-${ParticipantTabFormKey.city}`}
              label={getLabel(programSiteCoreKeys.addressDetailsCity)}
              placeholder={commonLabels.inputPlaceHolder}
              maxLength={MAX_LENGTH_50}
              showCloseIcon
              disabled={isCityDisable}
              requiredARIALabel={commonARIALabel.required}
              tooltipMessage={
                commonValidationMessages.fieldSpecialCharactersTooltipMessage
              }
              tooltipHeaderTitle={
                commonValidationMessages.allowedSpecialCharactersTitle
              }
            />
          </FormController>
        </MuiGrid>

        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <FormController
            control={control}
            name={ParticipantTabFormKey.stateProvince}
          >
            {stateProvinceRule.mandatory ||
            stateProvinceRule.showDropdownForOptionalCountry ? (
              <MuiSelect
                label={getLabel(
                  programSiteCoreKeys.addressDetailsStateProvince,
                )}
                options={countrySpecificOptions.stateOptions}
                visibleDefaultSelectOption
                value=""
                id={`${pageId}-${ParticipantTabFormKey.stateProvince}`}
                placeholder={commonLabels.selectPlaceHolder}
                requiredARIALabel={commonARIALabel.required}
                isMandatory={stateProvinceRule.mandatory}
              />
            ) : (
              <MuiTextField
                id={`${pageId}-${ParticipantTabFormKey.stateProvince}`}
                label={getLabel(
                  programSiteCoreKeys.addressDetailsStateProvince,
                )}
                placeholder={commonLabels.stateProvincePlaceHolder}
                maxLength={MAX_LENGTH_75}
                showCloseIcon
                requiredARIALabel={commonARIALabel.required}
                tooltipMessage={
                  commonValidationMessages.fieldSpecialCharactersTooltipMessage
                }
                tooltipHeaderTitle={
                  commonValidationMessages.allowedSpecialCharactersTitle
                }
              />
            )}
          </FormController>
        </MuiGrid>

        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <FormController
            control={control}
            name={ParticipantTabFormKey.postalCode}
            ref={postalCodeRef}
          >
            <TextFieldWithValidation
              label={getLabel(programSiteCoreKeys.addressDetailsPostalCode)}
              id={`${pageId}-${ParticipantTabFormKey.postalCode}`}
              formId={ParticipantTabFormKey.postalCode}
              isCountryChanged={countryIdWatch}
              zeroValidationMessage={fetchValidationMessage(
                commonSiteCoreKeys.greaterThanZeroFieldValidator,
              )}
              requiredFieldMessage={fetchValidationMessage(
                commonSiteCoreKeys.requiredValidation,
              )}
              isFormReset={false}
              fieldDetails={postalCodeFieldDetails}
              requiredARIALabel={commonARIALabel.required}
            />
          </FormController>
        </MuiGrid>
      </MuiGrid>
    </MuiBox>
  );
};
